/**
 * CreditNoteDetailModal
 * Modal for viewing credit note details with actions (validate, download, view PDF)
 * Similar to InvoiceDetailModal but for credit notes
 */

import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  PaperAirplaneIcon,
  EyeIcon,
  EnvelopeIcon,
  BuildingLibraryIcon,
} from '@heroicons/react/24/outline';
import { useFinalizeCreditNote, useCancelCreditNote } from '../../hooks/useCreditNotes';
import { downloadCreditNotePdf, previewCreditNotePdf } from '../../api/facturation.api';
import { PdfPreviewModal } from '../PdfPreviewModal/PdfPreviewModal';
import type { CreditNote, CreditNoteStatus } from '../../types/facturation.types';
import { formatDateShort } from '../../utils/dateFormat';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import '../../facturation.css';

// Status configuration
const STATUS_CONFIG: Record<CreditNoteStatus, { label: string; className: string }> = {
  DRAFT: { label: 'Brouillon', className: 'draft' },
  GENERATED: { label: 'Genere', className: 'generated' },
  SENT: { label: 'Envoye', className: 'sent' },
  CANCELLED: { label: 'Annule', className: 'cancelled' },
};

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

// Format date
const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return format(date, 'd MMMM yyyy', { locale: fr });
  } catch {
    return dateString;
  }
};

interface CreditNoteDetailModalProps {
  creditNote: CreditNote;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreditNoteDetailModal: React.FC<CreditNoteDetailModalProps> = ({
  creditNote,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [sendEmailOnValidate, setSendEmailOnValidate] = useState(true);

  const finalizeMutation = useFinalizeCreditNote();
  const cancelMutation = useCancelCreditNote();

  const isDraft = creditNote.status === 'DRAFT';
  const isGenerated = creditNote.status === 'GENERATED';
  const canValidate = isDraft;
  const canCancel = isDraft || isGenerated;

  // Handle PDF download
  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      // Use preview endpoint for drafts (generates PDF on-the-fly with watermark)
      // Use download endpoint for finalized credit notes (serves stored PDF)
      const blob = isDraft
        ? await previewCreditNotePdf(creditNote.id)
        : await downloadCreditNotePdf(creditNote.id);
      const url = window.URL.createObjectURL(blob);
      const a = globalThis.document.createElement('a');
      a.href = url;
      const fileName = creditNote.credit_note_number
        ? `note_credit_${creditNote.credit_note_number.replace(/\//g, '-')}.pdf`
        : `note_credit_brouillon_${creditNote.id}.pdf`;
      a.download = fileName;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle validate/finalize
  const handleValidate = async () => {
    setIsValidating(true);
    try {
      await finalizeMutation.mutateAsync({
        id: creditNote.id,
        sendEmail: sendEmailOnValidate
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Validate failed:', error);
    } finally {
      setIsValidating(false);
    }
  };

  // Handle cancel
  const handleCancel = async () => {
    const reason = prompt("Raison de l'annulation:");
    if (reason) {
      try {
        await cancelMutation.mutateAsync({ id: creditNote.id, reason });
        onSuccess?.();
        onClose();
      } catch (error) {
        console.error('Cancel failed:', error);
      }
    }
  };

  return (
    <>
      <Transition show={isOpen} as={React.Fragment}>
        <Dialog
          onClose={() => {
            if (!showPdfPreview) {
              onClose();
            }
          }}
          className="relative z-50"
        >
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform rounded-2xl bg-white shadow-xl transition-all flex flex-col">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                      <DocumentTextIcon className="h-6 w-6 text-red-600" />
                      <div>
                        <Dialog.Title className="text-lg font-semibold text-gray-900">
                          {isDraft ? 'Verifier la note de credit' : 'Details de la note de credit'}
                        </Dialog.Title>
                        <p className="text-sm text-gray-500">
                          {creditNote.credit_note_number || 'Brouillon'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="facturation-btn-modal-close"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-6 max-h-[60vh] overflow-y-auto">
                    <div className="space-y-6">
                      {/* Draft warning */}
                      {isDraft && (
                        <div className="fact-draft-warning">
                          <p>
                            <strong>Brouillon</strong> - Le numero de note de credit sera attribue lors de la validation.
                          </p>
                        </div>
                      )}

                      {/* Success message for draft */}
                      {isDraft && (
                        <div className="bg-green-50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                            <div>
                              <h3 className="font-medium text-green-900">Brouillon cree</h3>
                              <p className="text-sm text-green-700 mt-1">
                                Le brouillon a ete cree. Verifiez le PDF avant de valider.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Status badge (for non-draft) */}
                      {!isDraft && (
                        <div className="flex items-center gap-3">
                          <span className={`fact-status-badge ${STATUS_CONFIG[creditNote.status].className}`}>
                            {STATUS_CONFIG[creditNote.status].label}
                          </span>
                          {creditNote.validated_by && creditNote.validated_at && (
                            <span className="text-xs text-gray-500">
                              Valide par {creditNote.validator_first_name} {creditNote.validator_last_name} le {formatDateShort(creditNote.validated_at)}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Credit note summary */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Resume</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Numero:</span>
                            <span className="font-mono font-medium">{creditNote.credit_note_number || 'Brouillon'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Client:</span>
                            <span>{creditNote.billing_name || '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Date:</span>
                            <span>{formatDate(creditNote.created_at)}</span>
                          </div>
                          <div className="flex justify-between font-medium text-gray-900 border-t pt-2">
                            <span>Montant TVAC:</span>
                            <span className="text-red-600">-{formatCurrency(creditNote.total_tvac)}</span>
                          </div>
                          {creditNote.original_invoice_number && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Facture(s) liee(s):</span>
                              <span>{creditNote.original_invoice_number}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Email option (for validation) */}
                      {canValidate && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={sendEmailOnValidate}
                              onChange={(e) => setSendEmailOnValidate(e.target.checked)}
                              disabled={isValidating}
                              className="fact-email-checkbox"
                            />
                            <span className="text-sm text-gray-700">
                              Envoyer la note de credit par email au client
                            </span>
                          </label>
                          {creditNote.billing_email && (
                            <p className="text-xs text-gray-500 mt-1 ml-7">
                              Email: {creditNote.billing_email}
                            </p>
                          )}
                        </div>
                      )}

                      {/* PDF preview button */}
                      <div className="flex justify-center">
                        <button
                          onClick={() => setShowPdfPreview(true)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors disabled:opacity-50"
                        >
                          <EyeIcon className="h-5 w-5" />
                          {isDraft ? 'Previsualiser le brouillon' : 'Voir le PDF'}
                        </button>
                      </div>

                      {/* Next step info (for draft) */}
                      {canValidate && (
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <PaperAirplaneIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
                            <div>
                              <h3 className="font-medium text-blue-900">Prochaine etape</h3>
                              <p className="text-sm text-blue-700 mt-1">
                                Verifiez le PDF puis cliquez sur "Valider et envoyer" pour transmettre la note de credit a Falco.
                                {sendEmailOnValidate && creditNote.billing_email && (
                                  <span className="block mt-1">
                                    La note de credit sera egalement envoyee par email a <span className="font-medium">{creditNote.billing_email}</span>.
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Delivery status (non-draft only) */}
                      {!isDraft && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Statut de livraison</h4>
                          <div className="space-y-3">
                            {/* Falco status */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <BuildingLibraryIcon className="h-4 w-4 text-gray-500" />
                                <span className="text-sm text-gray-600">Falco/Horus</span>
                              </div>
                              <span className={`text-sm font-medium ${
                                creditNote.falco_status === 'SENT' || creditNote.falco_status === 'UPLOADED'
                                  ? 'text-green-600'
                                  : creditNote.falco_status === 'FAILED'
                                  ? 'text-red-600'
                                  : creditNote.falco_status === 'PENDING' || creditNote.falco_status === 'PROCESSING'
                                  ? 'text-amber-600'
                                  : 'text-gray-400'
                              }`}>
                                {creditNote.falco_status || 'Non envoye'}
                                {creditNote.falco_error && (
                                  <span className="text-xs text-red-500 block">{creditNote.falco_error}</span>
                                )}
                              </span>
                            </div>

                            {/* Email status */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <EnvelopeIcon className="h-4 w-4 text-gray-500" />
                                <span className="text-sm text-gray-600">Email client</span>
                              </div>
                              <span className={`text-sm font-medium ${
                                creditNote.email_sent
                                  ? 'text-green-600'
                                  : creditNote.email_error
                                  ? 'text-red-600'
                                  : 'text-gray-400'
                              }`}>
                                {creditNote.email_sent
                                  ? `Envoye${creditNote.email_recipient ? ` a ${creditNote.email_recipient}` : ''}${creditNote.email_sent_at ? ` le ${formatDateShort(creditNote.email_sent_at)}` : ''}`
                                  : creditNote.email_error
                                  ? `Erreur: ${creditNote.email_error}`
                                  : 'Non envoye'}
                              </span>
                            </div>

                            {/* Peppol status */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <svg className="h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
                                </svg>
                                <span className="text-sm text-gray-600">Peppol</span>
                              </div>
                              <span className={`text-sm font-medium ${
                                creditNote.peppol_status === 'DELIVERED'
                                  ? 'text-green-600'
                                  : creditNote.peppol_status === 'FAILED'
                                  ? 'text-red-600'
                                  : creditNote.peppol_status === 'PENDING'
                                  ? 'text-amber-600'
                                  : 'text-gray-400'
                              }`}>
                                {creditNote.peppol_status === 'NOT_REGISTERED'
                                  ? 'Client non inscrit'
                                  : creditNote.peppol_status || 'N/A'}
                                {creditNote.peppol_error && (
                                  <span className="text-xs text-red-500 block">{creditNote.peppol_error}</span>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
                    <button
                      onClick={onClose}
                      className="facturation-btn-modal-cancel disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Fermer
                    </button>

                    {/* Cancel button */}
                    {canCancel && (
                      <button
                        onClick={handleCancel}
                        disabled={cancelMutation.isPending}
                        className="facturation-btn-modal-danger inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Annuler
                      </button>
                    )}

                    {/* Validate button */}
                    {canValidate && (
                      <button
                        onClick={handleValidate}
                        disabled={isValidating}
                        className="facturation-btn-modal-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isValidating ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Envoi en cours...
                          </>
                        ) : (
                          <>
                            <PaperAirplaneIcon className="h-4 w-4" />
                            Valider et envoyer a Falco
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* PDF Preview Modal */}
      {showPdfPreview && (
        <PdfPreviewModal
          document={{
            type: 'creditNote',
            id: creditNote.id,
            number: creditNote.credit_note_number || 'Brouillon',
            date: creditNote.created_at,
            clientName: creditNote.billing_name || '-',
            amount: creditNote.total_tvac,
            status: creditNote.status,
            hasPdf: !!creditNote.pdf_path || isDraft,
          }}
          onClose={() => setShowPdfPreview(false)}
          onDownload={handleDownloadPdf}
          previewMode={isDraft}
          onBack={() => setShowPdfPreview(false)}
          backLabel="Retour aux details"
        />
      )}
    </>
  );
};

export default CreditNoteDetailModal;
